"use client";

import React, {
  createElement,
  CSSProperties,
  Fragment,
  useEffect,
  useRef,
} from "react";
import { autocomplete, AutocompleteOptions } from "@algolia/autocomplete-js";
import { createRoot } from "react-dom/client";
import classnames from "classnames";

import { getSourcesForEvaluationCopy } from "@/search/components/autocomplete/GetSources";
import { OnSelectParams } from "@algolia/autocomplete-shared/dist/esm/core/AutocompleteSource";
import { IndexDocument } from "@cappelendamm/catalogue/meilisearch/search-index-receiver.js";
import { BookItem } from "@/search/components/types";

import "@algolia/autocomplete-theme-classic";

export type AutocompleteEvaluationCopyProps = Partial<
  AutocompleteOptions<IndexDocument>
> & {
  onItemSelect: (book: BookItem) => void;
  className?: string;
};

export function AutocompleteEvaluationCopy({
  className,
  onItemSelect,
  ...autocompleteProps
}: AutocompleteEvaluationCopyProps) {
  const autocompleteContainer = useRef<HTMLDivElement>(null);
  const panelRootRef = useRef<ReturnType<typeof createRoot> | null>(null);
  const rootRef = useRef<
    Element | DocumentFragment | Document | ShadowRoot | null
  >(null);

  const onSelect = (onSelectParams: OnSelectParams<IndexDocument>) => {
    onSelectParams.setIsOpen(false);
    onSelectParams.setQuery("");
    onItemSelect(onSelectParams.item as BookItem);
  };

  const getSources = getSourcesForEvaluationCopy({
    onSelect,
  });

  useEffect(() => {
    if (!autocompleteContainer.current) return;
    const instance = autocomplete({
      ...autocompleteProps,
      container: autocompleteContainer.current,
      getSources: getSources,
      navigator: { navigate: () => {} }, // Hinders redirect
      renderer: {
        createElement,
        Fragment,
        render: () => {},
      },
      render: ({ children }, root) => {
        if (!panelRootRef.current || rootRef.current !== root) {
          rootRef.current = root;
          panelRootRef.current?.unmount();
          panelRootRef.current = createRoot(root);
        }
        panelRootRef.current.render(children);
      },
    });

    return () => {
      instance.destroy();
    };
  }, []);

  return (
    <div
      style={{ "--cvr-search-padding-x": 0 } as CSSProperties}
      className={classnames(
        "relative w-full rounded-[var(--cvr-search-border-radius)]",
        "[&_.aa-Form]:cvr-search!",
        "[&_.aa-Input]:[font-size:var(--cvr-search-font-size)]! [&_.aa-Input]:placeholder-[var(--cvr-search-placeholder)]!",
        "[&_.aa-SubmitIcon]:text-[var(--cvr-search-placeholder)]!",
        "[&_.aa-SubmitButton]:pl-4!",
        className,
      )}
      ref={autocompleteContainer}
    />
  );
}
